import { useId, CSSProperties } from 'react'

interface CrucibleProps {
  temperature?: number // in Celsius
  contents?: 'empty' | 'powder' | 'liquid' | 'solid'
  contentsColor?: string
  glowing?: boolean
  onHeat?: () => void
  onClick?: () => void
  disabled?: boolean
  className?: string
}

const Crucible = ({
  temperature = 25,
  contents = 'empty',
  contentsColor = '#A08060',
  glowing = false,
  onHeat,
  onClick,
  disabled = false,
  className = ''
}: CrucibleProps) => {
  const id = useId()

  // Calculate glow intensity based on temperature
  const getGlowColor = (temp: number): string => {
    if (temp < 400) return 'rgba(255, 90, 54, 0)'
    if (temp < 600) return 'rgba(255, 90, 54, 0.3)'
    if (temp < 800) return 'rgba(255, 120, 50, 0.5)'
    if (temp < 1000) return 'rgba(255, 150, 50, 0.7)'
    return 'rgba(255, 200, 100, 0.9)'
  }

  const getCeramicColor = (temp: number): string => {
    if (temp < 400) return '#E8E0D5'
    if (temp < 600) return '#E0C8A8'
    if (temp < 800) return '#D4A070'
    if (temp < 1000) return '#C87850'
    return '#FF6030'
  }

  const containerStyle: CSSProperties = {
    cursor: disabled ? 'default' : onClick || onHeat ? 'pointer' : 'default',
    opacity: disabled ? 0.5 : 1,
    transition: 'all 0.2s ease'
  }

  const isHot = temperature > 200
  const glowIntensity = glowing ? getGlowColor(temperature) : 'rgba(0,0,0,0)'

  return (
    <svg
      viewBox="0 0 80 90"
      width={70}
      height={80}
      onClick={disabled ? undefined : onClick || onHeat}
      style={containerStyle}
      className={className}
    >
      <defs>
        {/* Ceramic gradient - changes with temperature */}
        <linearGradient id={`ceramic-${id}`} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor={getCeramicColor(temperature)} />
          <stop offset="30%" stopColor={getCeramicColor(temperature + 50)} />
          <stop offset="70%" stopColor={getCeramicColor(temperature + 50)} />
          <stop offset="100%" stopColor={getCeramicColor(temperature)} />
        </linearGradient>

        {/* Interior shadow */}
        <radialGradient id={`interior-${id}`} cx="50%" cy="30%" r="70%">
          <stop offset="0%" stopColor={getCeramicColor(temperature - 100)} />
          <stop offset="100%" stopColor={getCeramicColor(temperature - 200)} />
        </radialGradient>

        {/* Contents gradient */}
        <radialGradient id={`contents-${id}`} cx="50%" cy="40%" r="60%">
          <stop offset="0%" stopColor={contentsColor} />
          <stop offset="100%" stopColor={contentsColor} stopOpacity="0.8" />
        </radialGradient>

        {/* Glow effect for hot crucible */}
        <filter id={`heat-glow-${id}`} x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="4" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>

        {/* Shadow */}
        <filter id={`shadow-${id}`} x="-20%" y="-10%" width="140%" height="130%">
          <feDropShadow dx="2" dy="3" stdDeviation="2" floodOpacity="0.3" />
        </filter>
      </defs>

      {/* Heat glow around crucible */}
      {glowing && temperature > 400 && (
        <ellipse
          cx="40"
          cy="50"
          rx="35"
          ry="30"
          fill={glowIntensity}
          filter={`url(#heat-glow-${id})`}
        >
          <animate
            attributeName="opacity"
            values="0.8;1;0.8"
            dur="1s"
            repeatCount="indefinite"
          />
        </ellipse>
      )}

      <g filter={`url(#shadow-${id})`}>
        {/* Crucible body - wider at top, narrower at bottom */}
        <path
          d="M15 25
             L12 55
             Q12 72 28 75
             L52 75
             Q68 72 68 55
             L65 25"
          fill={`url(#ceramic-${id})`}
          stroke={isHot ? '#AA6040' : '#C0B8A8'}
          strokeWidth="2"
        />

        {/* Rim - thickened top edge */}
        <ellipse
          cx="40"
          cy="25"
          rx="27"
          ry="8"
          fill={`url(#ceramic-${id})`}
          stroke={isHot ? '#AA6040' : '#B8B0A0'}
          strokeWidth="2"
        />

        {/* Inner rim highlight */}
        <ellipse
          cx="40"
          cy="25"
          rx="24"
          ry="6"
          fill="none"
          stroke={isHot ? '#C87850' : '#D8D0C0'}
          strokeWidth="1"
        />

        {/* Interior (visible from top) */}
        <ellipse
          cx="40"
          cy="28"
          rx="22"
          ry="5"
          fill={`url(#interior-${id})`}
        />

        {/* Contents */}
        {contents !== 'empty' && (
          <g>
            {contents === 'powder' && (
              <>
                <ellipse
                  cx="40"
                  cy="35"
                  rx="18"
                  ry="4"
                  fill={`url(#contents-${id})`}
                />
                {/* Powder texture */}
                {[...Array(8)].map((_, i) => (
                  <circle
                    key={i}
                    cx={28 + (i % 4) * 8}
                    cy={33 + Math.floor(i / 4) * 3}
                    r={1 + (i % 3) * 0.3}
                    fill={contentsColor}
                    opacity={0.6}
                  />
                ))}
              </>
            )}

            {contents === 'liquid' && (
              <>
                <ellipse
                  cx="40"
                  cy="32"
                  rx="20"
                  ry="5"
                  fill={contentsColor}
                  opacity="0.85"
                />
                {/* Liquid surface highlight */}
                <ellipse
                  cx="35"
                  cy="31"
                  rx="8"
                  ry="2"
                  fill="rgba(255,255,255,0.3)"
                />
              </>
            )}

            {contents === 'solid' && (
              <>
                <ellipse
                  cx="40"
                  cy="34"
                  rx="16"
                  ry="4"
                  fill={contentsColor}
                />
                <ellipse
                  cx="40"
                  cy="32"
                  rx="14"
                  ry="3"
                  fill={contentsColor}
                  opacity="0.9"
                />
              </>
            )}
          </g>
        )}

        {/* Ceramic texture / highlights */}
        <path
          d="M18 30 Q17 45 20 60"
          fill="none"
          stroke={isHot ? '#E89070' : 'rgba(255,255,255,0.4)'}
          strokeWidth="2"
          strokeLinecap="round"
        />

        {/* Secondary highlight */}
        <path
          d="M22 32 Q21 42 23 55"
          fill="none"
          stroke={isHot ? '#D87060' : 'rgba(255,255,255,0.2)'}
          strokeWidth="1"
          strokeLinecap="round"
        />

        {/* Bottom curve shadow */}
        <path
          d="M25 70 Q40 78 55 70"
          fill="none"
          stroke={isHot ? '#8A5030' : '#A8A090'}
          strokeWidth="1"
        />

        {/* Heat shimmer effect when very hot */}
        {temperature > 600 && (
          <g opacity="0.3">
            <line x1="30" y1="15" x2="32" y2="5" stroke="#FF8040" strokeWidth="1">
              <animate attributeName="y2" values="5;0;5" dur="0.5s" repeatCount="indefinite" />
            </line>
            <line x1="40" y1="15" x2="40" y2="3" stroke="#FF9050" strokeWidth="1">
              <animate attributeName="y2" values="3;-2;3" dur="0.6s" repeatCount="indefinite" />
            </line>
            <line x1="50" y1="15" x2="48" y2="5" stroke="#FF8040" strokeWidth="1">
              <animate attributeName="y2" values="5;0;5" dur="0.55s" repeatCount="indefinite" />
            </line>
          </g>
        )}
      </g>

      {/* Temperature indicator */}
      {isHot && (
        <g>
          <text
            x="40"
            y="82"
            fontSize="8"
            fill="#FF5A36"
            textAnchor="middle"
            fontFamily="Arial, sans-serif"
            fontWeight="bold"
          >
            {temperature}°C
          </text>
        </g>
      )}

      {/* Label */}
      <text
        x="40"
        y={isHot ? '90' : '85'}
        fontSize="8"
        fill="#E0E0E0"
        textAnchor="middle"
        fontWeight="500"
      >
        Crucible
      </text>
    </svg>
  )
}

export default Crucible
